import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Clock } from 'lucide-react';

interface HealthStatus {
  dataIntegrity: 'checking' | 'healthy' | 'warning' | 'critical';
  targetCompliance: boolean;
  duplicatesFound: number;
  stuckOperations: number;
  lastAutoCleanup: string | null;
  lastAutoResume: string | null;
  retryStats: {
    quiz: number;
    practice: number;
    enhancement: number;
    questions: number;
  };
  questionStats: {
    current: number;
    target: number;
    progress: number;
    duplicates: number;
  };
}

export const GenerationHealthDashboard = () => {
  const [health, setHealth] = useState<HealthStatus>({
    dataIntegrity: 'checking',
    targetCompliance: true,
    duplicatesFound: 0,
    stuckOperations: 0,
    lastAutoCleanup: null,
    lastAutoResume: null,
    retryStats: { quiz: 0, practice: 0, enhancement: 0, questions: 0 },
    questionStats: { current: 0, target: 100000, progress: 0, duplicates: 0 }
  });
  const [loading, setLoading] = useState(false);
  const [cronStatus, setCronStatus] = useState<{
    cleanup: boolean;
    autoResume: boolean;
  } | null>(null);

  const checkCronJobs = async () => {
    try {
      const { data, error } = await supabase.rpc('check_cron_jobs_status');
      if (!error && data && typeof data === 'object' && 'cleanup' in data && 'autoResume' in data) {
        setCronStatus({
          cleanup: !!data.cleanup,
          autoResume: !!data.autoResume
        });
      }
    } catch (error) {
      console.error('Failed to check cron jobs:', error);
    }
  };

  const checkSystemHealth = async () => {
    setLoading(true);
    try {
      // Check data integrity (including questions)
      const [quizCount, practiceCount, moduleCount, questionCount] = await Promise.all([
        supabase.from('module_quizzes').select('*', { count: 'exact', head: true }),
        supabase.from('module_practice').select('*', { count: 'exact', head: true }),
        supabase.from('course_modules').select('*', { count: 'exact', head: true }).eq('is_enabled', true),
        supabase.from('interview_questions').select('*', { count: 'exact', head: true })
      ]);

      const quizzes = quizCount.count || 0;
      const practices = practiceCount.count || 0;
      const modules = moduleCount.count || 0;
      const questions = questionCount.count || 0;

      // Question target is 100K
      const questionTarget = 100000;
      const questionProgress = (questions / questionTarget) * 100;

      let integrityStatus: 'healthy' | 'warning' | 'critical' = 'healthy';
      if (quizzes < modules * 0.5 || practices < modules * 0.5 || questions < questionTarget * 0.1) {
        integrityStatus = 'critical';
      } else if (quizzes < modules * 0.8 || practices < modules * 0.8 || questions < questionTarget * 0.5) {
        integrityStatus = 'warning';
      }

      // Check for stuck operations
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      const [stuckQuiz, stuckPractice, stuckEnhancement, stuckQuestions] = await Promise.all([
        supabase.from('quiz_generation_operations').select('id').eq('status', 'in_progress').lt('started_at', twoHoursAgo),
        supabase.from('practice_generation_operations').select('id').eq('status', 'in_progress').lt('started_at', twoHoursAgo),
        supabase.from('content_enhancement_operations').select('id').eq('status', 'in_progress').lt('started_at', twoHoursAgo),
        supabase.from('question_generation_operations').select('id').eq('status', 'in_progress').lt('started_at', twoHoursAgo)
      ]);

      const totalStuck = (stuckQuiz.data?.length || 0) + (stuckPractice.data?.length || 0) + 
                         (stuckEnhancement.data?.length || 0) + (stuckQuestions.data?.length || 0);

      // Check for duplicates in practice labs (count modules with multiple labs)
      const { data: allLabs } = await supabase
        .from('module_practice')
        .select('module_id');
      
      const moduleMap = new Map<string, number>();
      allLabs?.forEach(lab => {
        moduleMap.set(lab.module_id, (moduleMap.get(lab.module_id) || 0) + 1);
      });
      const duplicateCount = Array.from(moduleMap.values()).filter(count => count > 1).length;

      // Check for duplicate questions (should be 0 with UNIQUE constraint)
      const { data: allQuestions } = await supabase
        .from('interview_questions')
        .select('question_text');
      
      const questionTextSet = new Set<string>();
      let duplicateQuestions = 0;
      allQuestions?.forEach(q => {
        if (questionTextSet.has(q.question_text)) {
          duplicateQuestions++;
        } else {
          questionTextSet.add(q.question_text);
        }
      });

      const totalDuplicates = duplicateCount + duplicateQuestions;

      // Check retry counts
      const [quizRetries, practiceRetries, enhancementRetries, questionRetries] = await Promise.all([
        supabase.from('quiz_generation_operations').select('retry_count').gt('retry_count', 0),
        supabase.from('practice_generation_operations').select('retry_count').gt('retry_count', 0),
        supabase.from('content_enhancement_operations').select('retry_count').gt('retry_count', 0),
        supabase.from('question_generation_operations').select('retry_count').gt('retry_count', 0)
      ]);

      setHealth({
        dataIntegrity: integrityStatus,
        targetCompliance: quizzes >= modules && practices >= modules && questions >= questionTarget,
        duplicatesFound: totalDuplicates,
        stuckOperations: totalStuck,
        lastAutoCleanup: null, // Will be populated from logs
        lastAutoResume: null,
        retryStats: {
          quiz: quizRetries.data?.length || 0,
          practice: practiceRetries.data?.length || 0,
          enhancement: enhancementRetries.data?.length || 0,
          questions: questionRetries.data?.length || 0
        },
        questionStats: {
          current: questions,
          target: questionTarget,
          progress: questionProgress,
          duplicates: duplicateQuestions
        }
      });
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSystemHealth();
    checkCronJobs();
    const healthInterval = setInterval(checkSystemHealth, 60000); // Check every minute
    const cronInterval = setInterval(checkCronJobs, 60000); // Check every minute
    return () => {
      clearInterval(healthInterval);
      clearInterval(cronInterval);
    };
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'critical': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy': return <Badge variant="secondary" className="bg-green-500/10 text-green-500">Healthy</Badge>;
      case 'warning': return <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-500">Warning</Badge>;
      case 'critical': return <Badge variant="destructive">Critical</Badge>;
      default: return <Badge variant="outline">Checking...</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>System Health Monitor</CardTitle>
            <CardDescription>Real-time validation of generation integrity</CardDescription>
          </div>
          <Button
            onClick={checkSystemHealth}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Data Integrity */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.dataIntegrity)}
            <div>
              <p className="font-medium">Data Integrity</p>
              <p className="text-sm text-muted-foreground">
                All generated content verified in database
              </p>
            </div>
          </div>
          {getStatusBadge(health.dataIntegrity)}
        </div>

        {/* Target Compliance */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.targetCompliance ? 'healthy' : 'warning')}
            <div>
              <p className="font-medium">Target Compliance</p>
              <p className="text-sm text-muted-foreground">
                Operations stop correctly at target
              </p>
            </div>
          </div>
          {getStatusBadge(health.targetCompliance ? 'healthy' : 'warning')}
        </div>

        {/* Duplicates */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.duplicatesFound === 0 ? 'healthy' : 'warning')}
            <div>
              <p className="font-medium">Duplicate Detection</p>
              <p className="text-sm text-muted-foreground">
                {health.duplicatesFound} duplicate records found
              </p>
            </div>
          </div>
          {getStatusBadge(health.duplicatesFound === 0 ? 'healthy' : 'warning')}
        </div>

        {/* Stuck Operations */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(health.stuckOperations === 0 ? 'healthy' : 'critical')}
            <div>
              <p className="font-medium">Stuck Operations</p>
              <p className="text-sm text-muted-foreground">
                {health.stuckOperations} operations stuck &gt;2 hours
              </p>
            </div>
          </div>
          {getStatusBadge(health.stuckOperations === 0 ? 'healthy' : 'critical')}
        </div>

        {/* Question Generation Stats - NEW */}
        <div className="p-4 border rounded-lg bg-primary/5">
          <p className="font-medium mb-3 flex items-center gap-2">
            📊 Interview Question Generation
            <Badge variant="secondary" className="ml-auto">100K Target</Badge>
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current Progress:</span>
              <span className="font-medium">{health.questionStats.current.toLocaleString()} / 100,000</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Completion:</span>
              <span className="font-medium">{health.questionStats.progress.toFixed(1)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Duplicates Prevented:</span>
              <span className="font-medium text-green-500">
                {health.questionStats.duplicates === 0 ? '✓ UNIQUE Constraint Active' : `${health.questionStats.duplicates} found`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Auto-Retry Status:</span>
              <span className="font-medium">
                {health.retryStats.questions > 0 
                  ? `${health.retryStats.questions} operations retrying` 
                  : '✓ No retries needed'}
              </span>
            </div>
          </div>
        </div>

        {/* Automation Status */}
        <div className="p-4 border rounded-lg bg-primary/5">
          <p className="font-medium mb-3 flex items-center gap-2">
            🤖 Automation Status
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Auto-cleanup (every 5min):</span>
              <Badge variant={cronStatus?.cleanup ? "default" : "destructive"}>
                {cronStatus?.cleanup ? "Active ✓" : "Inactive"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Auto-resume (every 6min):</span>
              <Badge variant={cronStatus?.autoResume ? "default" : "destructive"}>
                {cronStatus?.autoResume ? "Active ✓" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Auto-Healing Stats */}
        <div className="p-4 border rounded-lg">
          <p className="font-medium mb-3">Auto-Healing Activity</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Quiz Retries:</span>
              <span className="ml-2 font-medium">{health.retryStats.quiz}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Practice Retries:</span>
              <span className="ml-2 font-medium">{health.retryStats.practice}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Enhancement Retries:</span>
              <span className="ml-2 font-medium">{health.retryStats.enhancement}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Question Retries:</span>
              <span className="ml-2 font-medium">{health.retryStats.questions}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
